import * as Sentry from "@sentry/browser";
import { getWebSocketStreamDomain } from "../../constants";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import TokenStore from "services/TokenStore";

type WSHandler = <T>(channel: string, data: T) => void;

interface HandlerEntry {
  prefix: string;
  handler: WSHandler;
}

type WebsocketEvent<T> = {
  channel: string;
  type: string;
  message?: string;
  data: T;
};

const DISCONNECT_DELAY_MS = 30000; // 30 seconds
const VISIBILITY_DISCONNECT_DELAY_MS = 15 * 60 * 1000; // 15 minutes

const WebSocketContext = createContext({
  registerHandler: (_prefix: string, _handler: WSHandler) => {},
  unregisterHandler: (_prefix: string, _handler: WSHandler) => {},
});

export const useWebSocketContext = () => {
  return useContext(WebSocketContext);
};

export default ({ children }: { children: ReactNode }) => {
  const handlers = useRef<HandlerEntry[]>([]);
  const ws = useRef<WebSocket | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityDisconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryDelayRef = useRef<number>(500); // Start with 500ms
  const isConnectingRef = useRef<boolean>(false);
  const shouldConnectRef = useRef<boolean>(false);

  const connect = useCallback(() => {
    if (
      isConnectingRef.current ||
      (ws.current && ws.current.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    isConnectingRef.current = true;

    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    console.debug("Attempting WebSocket connection...");
    const socket = new WebSocket(getWebSocketStreamDomain());
    ws.current = socket;

    socket.onopen = () => {
      console.debug("WebSocket connected");
      isConnectingRef.current = false;
      retryDelayRef.current = 500; // Reset delay on successful connection

      socket.send(
        JSON.stringify({
          type: "authenticate",
          accessToken: TokenStore.getLocalAccessToken()?.value,
        }),
      );
    };

    socket.onmessage = (event) => {
      let payload: WebsocketEvent<any>;
      try {
        payload = JSON.parse(event.data);
      } catch {
        console.warn("Invalid JSON:", event.data);
        return;
      }
      console.debug("Received message:", payload);
      if (payload.type === "system") {
        console.debug("System message:", payload.message);
        return;
      } else if (payload.type === "push") {
        const channel = payload.channel;
        if (!channel) return;

        handlers.current.forEach(({ prefix, handler }) => {
          if (channel.startsWith(prefix)) {
            handler(channel, payload.data);
          }
        });
      }
    };

    socket.onclose = (event) => {
      const closeReason =
        event.code === 1006
          ? "Abnormal closure (network issue, server restart, etc.)"
          : event.reason || "Unknown reason";

      console.debug(`WebSocket closed ${event.code}: ${closeReason}`);
      isConnectingRef.current = false;

      // Only retry if we should be connected (have handlers)
      if (shouldConnectRef.current && handlers.current.length > 0) {
        const delay = retryDelayRef.current;
        console.debug(`Retrying connection in ${delay}ms`);

        retryTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);

        // Exponential backoff: double the delay, max 10 seconds
        retryDelayRef.current = Math.min(delay * 2, 10000);
      }
    };

    socket.onerror = (event) => {
      // The error event itself doesn't contain much useful information
      // The real details come in the subsequent onclose event
      console.debug(
        "WebSocket error occurred - details will follow in close event",
      );
      isConnectingRef.current = false;
      Sentry.captureException(new Error("WebSocket error"), {
        extra: { event },
      });
    };
  }, []);

  const disconnect = useCallback(() => {
    shouldConnectRef.current = false;

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "hidden") {
      // Start disconnect timer when page becomes hidden
      if (visibilityDisconnectTimeoutRef.current) {
        clearTimeout(visibilityDisconnectTimeoutRef.current);
      }

      visibilityDisconnectTimeoutRef.current = setTimeout(() => {
        console.debug("Disconnecting WebSocket due to inactivity");
        disconnect();
      }, VISIBILITY_DISCONNECT_DELAY_MS);
    } else if (document.visibilityState === "visible") {
      // Clear disconnect timer and reconnect immediately when page becomes visible
      if (visibilityDisconnectTimeoutRef.current) {
        clearTimeout(visibilityDisconnectTimeoutRef.current);
        visibilityDisconnectTimeoutRef.current = null;
      }

      // Reconnect if we have handlers and should be connected
      if (handlers.current.length > 0) {
        shouldConnectRef.current = true;
        connect();
      }
    }
  }, [connect, disconnect]);

  useEffect(() => {
    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      // Cleanup on unmount
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }

      if (visibilityDisconnectTimeoutRef.current) {
        clearTimeout(visibilityDisconnectTimeoutRef.current);
        visibilityDisconnectTimeoutRef.current = null;
      }

      disconnect();
    };
  }, [disconnect, handleVisibilityChange]);

  const registerHandler = (prefix: string, handler: WSHandler) => {
    // Clear any pending disconnect timeout
    if (disconnectTimeoutRef.current) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }

    handlers.current.push({ prefix, handler });

    // Lazy connect: only connect when first handler is registered
    if (handlers.current.length === 1) {
      shouldConnectRef.current = true;
      connect();
    }
  };

  const unregisterHandler = (prefix: string, handler: WSHandler) => {
    handlers.current = handlers.current.filter(
      (h) => !(h.prefix === prefix && h.handler === handler),
    );

    // Disconnect after delay when no handlers remain
    if (handlers.current.length === 0) {
      disconnectTimeoutRef.current = setTimeout(() => {
        disconnect();
      }, DISCONNECT_DELAY_MS);
    }
  };

  return (
    <WebSocketContext.Provider value={{ registerHandler, unregisterHandler }}>
      {children}
    </WebSocketContext.Provider>
  );
};
